import * as Sentry from '@sentry/react';
import { selectIceRelayRequired } from 'features/application/applicationSlice';
import { SFrameManager } from 'features/e2ee/SFrameManager';
import {
  publisherLeft,
  receivingPluginCleanup,
  remoteTrackReceived,
  subscriberAttached,
  videoroomError,
} from 'features/streaming/actions';
import { FeedId, SubscriberMid, SubscribeStreamList } from 'features/streaming/types';
import Janus, { JanusJS } from 'lib/janus';
import { store } from 'store/store';
import { noop } from 'utils/flow';
import { formatErrorForLogging, logger } from 'utils/logger';
import {
  ControlledReceivingHandle,
  VideoroomConnectionState,
} from 'utils/webrtc/ControlledReceivingHandle';
import { GENERIC_JANUS_ERROR } from 'utils/webrtc/errors';
import { RTCClient, StreamSubscriptionState } from 'utils/webrtc/index';
import {
  iceRestartTemplate,
  joinAsSubscriberTemplate,
  sendMessage,
  startSubscriptionTemplate,
  subscribeTemplate,
  unsubscribeTemplate,
} from 'utils/webrtc/messages';
import { BaseReceiver } from 'utils/webrtc/receiving/BaseReceiver';
import { JanusConnection } from 'utils/webrtc/types';
import { handleAttachError } from 'utils/webrtc/handleAttachError';
import { logJanusHandleAttached } from 'utils/webrtc/logJanusHandleAttached';
import { refreshSubscribeGrant } from 'utils/webrtc/refreshSubscribeGrant';
import {
  isSubscribeGrantRefusal,
  RejectedJoinCode,
  SUBSCRIBE_GRANT_REFUSED_CODE,
} from 'utils/webrtc/subscribeGrantError';
import { emitRejoinGrant, emitSubscribeRefused } from 'utils/webrtc/subscribeTelemetry';
import { v4 as uuidv4 } from 'uuid';

export const SUBSCRIBE_FAILURES_BEFORE_CAPTURE = 3;

export class VideoroomReceiver extends BaseReceiver {
  queue: Record<string, SubscribeStreamList> = {};

  sentRequests: Record<string, any[]> = {};

  // Handle whose events we accept. A rebuild flips it before detaching the wedged handle, so
  // the old PC's `disconnected`/`oncleanup` cannot re-enter the state machine against the new PC.
  private activeHandleId?: string;

  attachPlugin = (pluginHandle: string, connection: JanusConnection) => {
    this.connection = connection;
    this.activeHandleId = pluginHandle;
    this.onNewPeerConnection();

    const forActiveHandle =
      <A extends unknown[]>(handler: (...args: A) => void) =>
      (...args: A) => {
        if (this.activeHandleId === pluginHandle) {
          handler(...args);
        }
      };

    connection.janus.attach({
      plugin: 'janus.plugin.videoroom',
      opaqueId: connection.handle,
      success: async (videoroom) => {
        if (this.activeHandleId !== pluginHandle) {
          videoroom.detach();
          return;
        }

        this.plugin = new ControlledReceivingHandle(videoroom, pluginHandle, connection);

        logJanusHandleAttached('videoroom-subscriber', videoroom, connection.janus);

        this.consumeQueue(connection.handle);
      },
      error: handleAttachError('attach_receiving_feed'),
      consentDialog: noop,
      onmessage: forActiveHandle(this.onMessage),
      onlocaltrack: noop,
      onremotetrack: forActiveHandle((track, mid, on, metadata) => {
        if (on) {
          const feedId = this.feed.feedIdByMid[mid];
          const existingStream = this.feed.media.subscriberStream(feedId, mid);
          const reason = metadata?.reason || 'unknown';

          logger
            .remote({ system: true, capture: 'streaming' })
            .debug(`[STREAM-DEBUG] onremotetrack: ${track.kind} track added (${reason})`, {
              mid,
              trackId: track.id,
              trackKind: track.kind,
              trackReadyState: track.readyState,
              trackEnabled: track.enabled,
              trackMuted: track.muted,
              feedId,
              reason,
              hasExistingTrack: !!existingStream?.hasTrack,
              existingTrackId: existingStream?.getTrack()?.id,
              existingSubscriptionState: existingStream?.subscriptionState,
            });

          store.dispatch(remoteTrackReceived({ pluginHandle, track, mid }));

          // Dump diagnostics after track is added
          setTimeout(() => {
            this.feed.logMediaStreamsDiagnostics(
              `After onremotetrack(on=true) for ${track.kind} mid=${mid}, feedId=${feedId}`,
              undefined,
              { feedId, mid }
            );
          }, 300);
        } else {
          const feedId = this.feed.feedIdByMid[mid];
          const streamMedia = this.feed.media.subscriberStream(feedId, mid);
          const reason = metadata?.reason || 'unknown';
          logger
            .remote({ system: true, capture: 'streaming' })
            .debug(`[STREAM-DEBUG] onremotetrack: ${track.kind} track removed (${reason})`, {
              mid,
              trackId: track.id,
              trackKind: track.kind,
              feedId,
              reason,
              hasStreamMedia: !!streamMedia,
              streamMediaHasTrack: streamMedia?.hasTrack,
              existingTrackId: streamMedia?.getTrack()?.id,
              existingTrackReadyState: streamMedia?.getTrack()?.readyState,
              hasAudioElement: !!this.feed.audioElements[mid],
            });

          // Dump diagnostics after track is removed
          setTimeout(() => {
            this.feed.logMediaStreamsDiagnostics(
              `After onremotetrack(on=false) for ${track.kind} mid=${mid}, feedId=${feedId}`,
              undefined,
              { feedId, mid }
            );
          }, 300);
        }
      }),
      oncleanup: forActiveHandle(() => {
        this.onPeerConnectionGone();
        store.dispatch(receivingPluginCleanup({ pluginHandle, instanceId: this.feed.instanceId }));
      }),
      webrtcState: forActiveHandle((on, reason) => {
        Janus.log(`Receiving Feed: WebRTC PeerConnection is ${on ? 'up' : 'down'} now`);
        if (on) {
          this.onWebRTCConnectionUp();
        } else {
          this.onWebRTCConnectionDown(reason);
        }
      }),
      iceState: forActiveHandle(this.onIceState),
      iceGatheringState: forActiveHandle(this.onIceGatheringState),
      connectionState: forActiveHandle(this.onConnectionState),
      slowLink: (uplink, lost, mid) => {
        Janus.warn(
          `Janus reports problems (${
            uplink ? 'sending' : 'receiving'
          }) packets on mid ${mid} (${lost} lost packets)`
        );
      },
      sframe: SFrameManager.getClient(),
    });
  };

  private onMessage = (
    message: JanusJS.EvtMessage,
    messageJsep?: JanusJS.JSEP,
    eventBody?: JanusJS.EvtBody
  ) => {
    const { plugin } = this;

    if (!plugin) {
      return;
    }

    logger
      .remote({ system: true, capture: 'streaming', tier: 1 })
      .debug(`[Videoroom Receiver] Got a remote message in receiving feed:`, message);
    if (messageJsep) {
      plugin.janusPlugin.createAnswer({
        jsep: messageJsep,
        tracks: [
          { type: 'audio', recv: true },
          { type: 'video', recv: true },
        ],
        // Gate on the live SFrame client, not the E2EE flag: no decrypt pump means zero RTP.
        externalEncryption: !!SFrameManager.getClient(),
        // Only the first answer of a handle creates the PC; a remembered relay decision must
        // not race the direct path again.
        iceTransportPolicy: selectIceRelayRequired(store.getState()) ? 'relay' : undefined,
        success: async (jsep: JanusJS.JSEP) => {
          try {
            await sendMessage(
              plugin.janusPlugin,
              startSubscriptionTemplate(RTCClient.roomId!),
              jsep
            );
          } catch (e) {
            if (!RTCClient.supressErrors) {
              logger.remote({ system: true, capture: 'streaming' }).error(e);
            }
          }
        },
        error: GENERIC_JANUS_ERROR('receiving_feed'),
      });
    }

    if (message.streams) {
      this.feed.handleSubscriptionsUpdate(plugin, message.streams);
    }

    const event = message.videoroom;
    const { handle } = plugin;

    switch (event) {
      case 'attached': {
        if (eventBody?.transaction) {
          delete this.sentRequests[eventBody.transaction];
        }

        store.dispatch(
          subscriberAttached({
            handle,
            message: null,
          })
        );
        break;
      }
      case 'leaving': {
        store.dispatch(
          publisherLeft({
            handle: this.feed.handle,
            message: message.leaving,
          })
        );
        break;
      }
      case 'event': {
        const { substream, temporal, mid } = message;

        if (
          (substream !== null && substream !== undefined) ||
          (temporal !== null && temporal !== undefined)
        ) {
          logger.debug('[Videoroom Receiver] Simulcast substream update: ', substream);
          if (temporal) {
            logger.debug('[Videoroom Receiver] Simulcast temporal update: ', temporal);
          }
          this.feed.updateSimulcastValue(mid, substream, temporal);
        } else if (message.started) {
          logger
            .remote({ system: true, capture: 'streaming' })
            .debug(`[Videoroom Receiver] ${handle} started`);
        } else if (message.configured) {
          logger
            .remote({ system: true, capture: 'streaming', tier: 1 })
            .debug(`[Videoroom Receiver] ${handle} configured`);
        } else if (message.unpublished && message.unpublished !== 'ok') {
          // maybe we don't need this one;
          logger
            .remote({ system: true, capture: 'streaming' })
            .error('[Videoroom Receiver] Receiving Feed unpublished', message.unpublished);

          store.dispatch(
            publisherLeft({
              handle: this.feed.handle,
              message: message.unpublished,
            })
          );
        } else if (message.error_code) {
          store.dispatch(
            videoroomError({
              handle,
              message: { error_code: message.error_code, error: String(message.error ?? '') },
              eventBody,
              source: 'receiver',
            })
          );
        } else {
          const messageStr = '[Videoroom Receiver] Got an anomalous message in receiving feed';

          logger.remote({ system: true, capture: 'streaming' }).warn(messageStr, message);
        }
        break;
      }
      case 'updating': {
        const droppedStreams = eventBody?.transaction
          ? this.sentRequests[eventBody.transaction]
          : undefined;

        if (droppedStreams?.length) {
          const logData = {
            handleId: handle,
            transaction: eventBody?.transaction,
            droppedFeeds: droppedStreams.map((stream) => stream.feed),
            droppedMids: droppedStreams.map((stream) => stream.mid),
            connectionState: VideoroomConnectionState[plugin.connectionState],
            queuedSubscriptions: plugin.pendingSubscriptions.length,
          };

          const errorMessage =
            '[RENEGOTIATION-DEFERRED] Janus is busy; the subscription is dropped';

          logger.remote({ system: true, capture: 'streaming' }).warn(errorMessage, logData);

          Sentry.captureMessage(errorMessage, {
            level: 'warning',
            contexts: { deferredSubscription: logData },
          });
        }

        break;
      }
      case 'updated': {
        if (eventBody?.transaction) {
          delete this.sentRequests[eventBody.transaction];
        }

        break;
      }
      default:
        logger
          .remote({ system: true, capture: 'streaming' })
          .warn('[Videoroom Receiver] Got an anomalous message in receiving feed', message);
    }
  };

  private consecutiveSubscribeFailures = 0;

  private handleSubscriptionSent = (feed: ControlledReceivingHandle) => {
    if (this.consecutiveSubscribeFailures) {
      logger
        .remote({ system: true, capture: 'streaming' })
        .info('[Videoroom Receiver] Subscribe request recovered after a retry', {
          handle: feed.handle,
          connectionState: VideoroomConnectionState[feed.connectionState],
          attempts: this.consecutiveSubscribeFailures,
        });

      this.consecutiveSubscribeFailures = 0;
    }
  };

  private requeueSubscription = (feed: ControlledReceivingHandle, streams: SubscribeStreamList) => {
    const retryable = streams.filter((stream) => {
      if (!stream.mid) {
        return true;
      }

      const streamMedia = this.feed.media.existingStream(stream.feed, stream.mid);

      if (!streamMedia) {
        return false;
      }

      // `subscribe` stamps every stream `connecting` before it knows the outcome.
      // A stream that already has its track is subscribed, and leaving the stamp
      // on would make `canSubscribe` false for the rest of the call.
      if (streamMedia.hasTrack) {
        streamMedia.setSubscriptionState(StreamSubscriptionState.connected);

        return false;
      }

      streamMedia.setSubscriptionState(StreamSubscriptionState.created);

      return true;
    });

    if (retryable.length) {
      feed.enqueueSubscription(retryable);
    }

    return retryable.length;
  };

  private reportSubscribeRefused = (
    feed: ControlledReceivingHandle,
    streams: SubscribeStreamList,
    error: unknown
  ) => {
    if (RTCClient.supressErrors || this.negotiationMeta.mediaFailureReported) {
      return;
    }

    emitSubscribeRefused(
      { code: SUBSCRIBE_GRANT_REFUSED_CODE, handle: feed.handle, streamCount: streams.length },
      error
    );

    this.surfaceMediaFailure('connection_failed');
  };

  private handleFailedSubscription = (
    feed: ControlledReceivingHandle,
    streams: SubscribeStreamList,
    error: unknown
  ) => {
    // The fork refuses a spent grant on every retry, so re-queuing would only loop.
    if (isSubscribeGrantRefusal(error)) {
      this.reportSubscribeRefused(feed, streams, error);
      return;
    }

    const requeued = this.requeueSubscription(feed, streams);

    if (requeued > 0) {
      this.consecutiveSubscribeFailures += 1;
    }

    // A teardown makes every in-flight request fail; reporting those would bury
    // the real losses this telemetry exists to surface.
    if (RTCClient.supressErrors) {
      return;
    }

    const context = {
      handle: feed.handle,
      connectionState: VideoroomConnectionState[feed.connectionState],
      attempt: this.consecutiveSubscribeFailures,
      requeued,
      streams: streams.map(({ feed: feedId, mid }) => ({ feedId, mid })),
    };

    logger
      .remote({ system: true, capture: 'streaming' })
      .warn('[Videoroom Receiver] Subscribe request failed', context);

    if (requeued > 0 && this.consecutiveSubscribeFailures === SUBSCRIBE_FAILURES_BEFORE_CAPTURE) {
      const message = '[Videoroom Receiver] Subscribe retries are not recovering';

      Sentry.captureException(new Error(message, { cause: error }), {
        tags: { component: 'VideoroomReceiver', operation: 'subscribe' },
        contexts: { subscribeFailure: { ...context, ...formatErrorForLogging(error) } },
      });
    }
  };

  subscribe = async (connectionHandle: string, streams: SubscribeStreamList) => {
    const feed = this.plugin;

    if (feed && streams.length) {
      let roomUsers = this.feed.media.feedsWithVideo.length;
      const subscribeFeeds: string[] = [];

      streams = streams.map((stream) => {
        if (!subscribeFeeds.includes(stream.feed)) {
          subscribeFeeds.push(stream.feed);
          roomUsers += 1;
        }

        const [substream, temporal] = this.feed.getBaseSimulcastConfig(roomUsers);

        if (stream.mid) {
          const streamMedia = this.feed.media.stream(stream.feed, stream.mid);

          streamMedia.setSubscriptionState(StreamSubscriptionState.connecting);
        }

        return { ...stream, substream, temporal };
      });

      switch (feed.connectionState) {
        case VideoroomConnectionState.connected: {
          if (this.plugin) {
            try {
              const { request } = await sendMessage(
                this.plugin?.janusPlugin,
                subscribeTemplate(streams)
              );

              if (request.transaction && request.body?.streams) {
                this.sentRequests[request.transaction] = request.body.streams;
              }

              this.handleSubscriptionSent(feed);
              this.plugin?.processQueue();
            } catch (error) {
              this.handleFailedSubscription(feed, streams, error);
            }
          }
          break;
        }
        case VideoroomConnectionState.connecting: {
          logger
            .remote({ system: true, capture: 'streaming' })
            .debug(
              `[Videoroom Receiver] Feed ${feed.handle} is in connecting state, will subscribe later`
            );

          this.requeueSubscription(feed, streams);
          break;
        }
        case VideoroomConnectionState.created: {
          feed.setConnectionState(VideoroomConnectionState.connecting);

          try {
            const { request } = await sendMessage(
              feed.janusPlugin,
              joinAsSubscriberTemplate(
                RTCClient.roomId!,
                RTCClient.roomPin!,
                streams,
                RTCClient.privateId
              )
            );

            if (request.transaction && request.body?.streams) {
              this.sentRequests[request.transaction] = request.body.streams;
            }

            this.handleSubscriptionSent(feed);
          } catch (error) {
            this.handleFailedSubscription(feed, streams, error);
          }

          break;
        }
        case VideoroomConnectionState.disconnecting:
        case VideoroomConnectionState.disconnected: {
          // Leaving a room drops these requests by design; only a handle that
          // died under a live client is worth hearing about.
          if (!RTCClient.supressErrors) {
            logger
              .remote({ system: true, capture: 'streaming' })
              .warn('[Videoroom Receiver] Dropping a subscribe request for a handle that is gone', {
                handle: feed.handle,
                connectionState: VideoroomConnectionState[feed.connectionState],
                streams: streams.map(({ feed: feedId, mid }) => ({ feedId, mid })),
              });
          }

          break;
        }
        default:
          throw new Error('Unexpected connection state');
      }
    } else {
      this.queueSubscription(connectionHandle, streams);
    }
  };

  unsubscribe = (feedId: FeedId, mid?: SubscriberMid) => {
    const feed = this.plugin;
    if (!feed) {
      return;
    }

    // A handle in `created` has no confirmed join (join is sent on first subscribe),
    // so Janus would reject the unsubscribe with a 424.
    if (feed.connectionState === VideoroomConnectionState.created) {
      logger
        .remote({ system: true, capture: 'streaming', tier: 1 })
        .debug(
          `[Videoroom Receiver] Skipping unsubscribe from feed ${feedId}: handle ${feed.handle} is not in a joined state`
        );
      return;
    }

    feed.janusPlugin.send({
      message: unsubscribeTemplate([{ feed: feedId, sub_mid: mid }]),
    });
  };

  private queueSubscription = (connectionHandle: string, streams: SubscribeStreamList) => {
    if (this.queue[connectionHandle]) {
      this.queue[connectionHandle] = this.queue[connectionHandle].concat(streams);
    } else {
      this.queue[connectionHandle] = streams;
    }
  };

  private consumeQueue = (connectionHandle: string) => {
    if (!this.queue[connectionHandle]?.length) {
      return;
    }

    this.subscribe(connectionHandle, this.queue[connectionHandle]);

    this.queue[connectionHandle] = [];
  };

  iceRestartHandler = async (feed: ControlledReceivingHandle) => {
    await sendMessage(feed.janusPlugin, iceRestartTemplate());
  };

  // Never-established rebuild: the wedged handle is dead server-side, so the PC comes back on a
  // fresh handle's `join`, not an ICE restart; `trigger` is unused but keeps the driver's call typed.
  rebuildHandler: NonNullable<BaseReceiver['rebuildHandler']> = async (
    feed: ControlledReceivingHandle
  ): Promise<boolean> => {
    const { connection } = feed;
    const streams = this.feed.streamsForRejoin();

    // Gate the wedged handle out before the grant round-trip: on a Janus hangup its `oncleanup`
    // lands during that await and would otherwise cancel the rebuild deadline just armed.
    const wedgedHandle = this.activeHandleId;
    const replacementHandle = uuidv4();
    this.activeHandleId = replacementHandle;

    // The grant a `join` spends is single-use, and mintable grants are rate-limited server-side —
    // so refresh before the swap that turns the queued subscription into a `join`, and only then.
    if (streams.length) {
      try {
        await refreshSubscribeGrant();
      } catch (error) {
        this.activeHandleId = wedgedHandle;
        throw error;
      }
    }

    // Setting `disconnecting` also cancels the handle's own connect timeout, leaving the
    // driver's rebuild-connect deadline as the only one still running.
    const detachWedgedHandle = () => {
      feed.setConnectionState(VideoroomConnectionState.disconnecting);
      feed.janusPlugin.detach();
    };

    this.queueSubscription(connection.handle, streams); // no-op when empty
    this.attachPlugin(replacementHandle, connection);
    detachWedgedHandle();

    return streams.length > 0;
  };

  private rejoinRecovery?: Promise<void>;

  // Our Janus fork spends the single-use grant even on a plugin-rejected `join`, so the re-join
  // needs a fresh one. Coalesced whole: a second reset would reopen an in-flight re-join.
  recoverFromRejectedJoin = (feed: ControlledReceivingHandle, code: RejectedJoinCode) => {
    this.rejoinRecovery ??= this.rejoinOnFreshGrant(feed, code).finally(() => {
      this.rejoinRecovery = undefined;
    });

    return this.rejoinRecovery;
  };

  private rejoinOnFreshGrant = async (feed: ControlledReceivingHandle, code: RejectedJoinCode) => {
    let error: string | null = null;

    try {
      await refreshSubscribeGrant();
    } catch (refreshError) {
      error = formatErrorForLogging(refreshError).message;
    }

    // The mint round trip can be crossed by a teardown, a client reset or the connecting timer.
    const handleReset =
      feed.connectionState === VideoroomConnectionState.connecting &&
      RTCClient.receivingFeed.receiver?.plugin === feed;

    if (handleReset) {
      feed.setConnectionState(VideoroomConnectionState.created);
    }

    if (!RTCClient.supressErrors) {
      emitRejoinGrant({
        outcome: error === null ? 'refreshed' : 'token_refresh_failed',
        code,
        handleReset,
        error,
      });
    }
  };

  retrySubscription = (connectionHandle: string, transaction: string, errorMessage: string) => {
    const excludedFeed = errorMessage.match(/\(([^)]+)\)/)?.[1];
    if (excludedFeed && this.sentRequests[transaction]) {
      logger
        .remote({ system: true, capture: 'streaming' })
        .warn('[Videoroom Receiver] Retrying subscription, excluding feed:', excludedFeed);

      this.subscribe(
        connectionHandle,
        this.sentRequests[transaction].filter((stream) => stream.feed !== excludedFeed)
      );

      delete this.sentRequests[transaction];
    }
  };
}
